/**
 * Wall-hung props — framed art and the towel on its rod.
 *
 * Two rules here are not negotiable, and both are lessons the dorm planner
 * paid for in measured defects:
 *
 *  - Position and facing come from ONE call, `inwardFacingAt`. A wall is a flat
 *    box, so a stored `rotation.y` of 0 and of pi describe the same wall and a
 *    scan records whichever matched the capture; 268 of 290 dev rooms have
 *    walls that disagree with each other. Taking the offset from one source and
 *    the rotation from another is what put 59% of dorm's wall decor facing into
 *    the wall.
 *  - Sample at the wall MIDPOINT, never at the piece's own position:
 *    `inwardNormalAt` points at the floor centroid and is only perpendicular
 *    there, so sampling off-centre tilts the offset and slides the piece into
 *    the wall face.
 */

const {
  freeSpansOnWall,
  projectToParam,
} = require('../../dorm-styling/plan/wallSpans');
const {
  wallAxesFor,
  inwardFacingAt,
  inwardFacingAtStrict,
  getFloorPolygon,
  getFloorY,
  pointToSegmentDistanceXZ,
  DEFAULT_WALL_THICKNESS_M,
} = require('../../dorm-styling/dormDecorGeometry');
const { footprintFor } = require('../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../bathroom-analyzer/pipeline/geometry');
const { stagingPropDimsM } = require('../propDims');
const { objectTypeOf } = require('../ownership');
const { planDims } = require('./surfaces');

const SPAN_EPS = 1e-6;
const {
  WALL_END_MARGIN_M,
  AVOID_SEVERITY,
  AGAINST_WALL_M,
  FLOOR_WALL_GAP_M,
  FLOOR_SAMPLE_STEP_M,
} = require('./constants');

/**
 * Rank the room's free wall runs, widest first — ONE CANDIDATE PER RUN, not
 * per wall. `freeSpansOnWall` already subtracts doors, windows and furniture
 * that reaches the hanging band, so this is "where could a piece actually
 * go". A wall with a toilet at one end and a vanity at the other offers two
 * runs; keeping only its largest threw the sink-end run away whenever the
 * toilet-end run was longer by a few centimetres, and the ring that wanted
 * it was dropped. `hangOnWall` still claims the WALL, so two runs of one wall
 * never both get a piece unless the piece may share a wall.
 */
function rankWallsByFreeRun(room, walls, endpointsById) {
  const out = [];
  for (const wall of walls || []) {
    const resolved = wallAxesFor(wall, endpointsById);
    if (!resolved?.axes) continue;
    for (const span of freeSpansOnWall(
      wall,
      resolved.axes,
      room,
      endpointsById
    )) {
      out.push({ wall, axes: resolved.axes, span });
    }
  }
  out.sort((a, b) => b.span.length - a.span.length);
  return out;
}

/** Perpendicular distance from a point to a wall's centreline, in plan. */
function perpToWallLine(point, axes) {
  return Math.abs(
    (point.x - axes.start.x) * axes.wallDir.z -
      (point.z - axes.start.z) * axes.wallDir.x
  );
}

/**
 * How far an object stands off a wall's inner FACE — 0 when it is against it.
 */
function objectGapToWall(obj, cand) {
  // A scanned record with no box is not against anything.
  if (!obj?.center || !obj?.dimensions) return Infinity;
  const f = footprintFor(
    obj.center,
    obj.dimensions.x,
    obj.dimensions.z,
    obj?.rotation?.y || 0
  );
  if (!f) return Infinity;
  const half = (cand.wall?.dimensions?.z ?? DEFAULT_WALL_THICKNESS_M) / 2;
  return Math.min(
    ...f.map((p) => Math.max(0, perpToWallLine(p, cand.axes) - half))
  );
}

/**
 * Which wall each of these objects stands against — its NEAREST, and only
 * that one.
 *
 * "Above the toilet" is a claim about ONE wall. A corner fixture is close to
 * two, and counting it on both lets the preference be satisfied by a wall the
 * toilet merely happens to be near — which is how the art ended up over the
 * vanity in a room whose toilet was on the opposite side.
 */
function attributeToWalls(objects, candidates) {
  const wallOf = new Map();
  for (const obj of objects) {
    let best = null;
    for (const cand of candidates) {
      const d = objectGapToWall(obj, cand);
      if (!best || d < best.d) best = { d, id: cand.wall.id };
    }
    if (best) wallOf.set(obj, best.id);
  }
  return wallOf;
}

/**
 * The top of a wall's FACE at a point along it, in world y.
 *
 * A wall is not a rectangle. Under a roof it is a triangle or a pentagon, and
 * `dimensions.y` is only its tallest point — on one corpus loft bathroom a
 * wall is 51 in at one end and ZERO at the other, and the planner hung a towel
 * at 58 in and a framed print at 91 in on it, both floating in the air above
 * the wall's own top edge.
 *
 * `polygonCorners` are world-space with y as height, so the face's upper
 * envelope is read directly: project the corners onto the wall axis, then take
 * the highest edge spanning the query point.
 */
function wallFaceTopAt(wall, axes, t) {
  const corners = wall?.polygonCorners;
  if (!Array.isArray(corners) || corners.length < 3) {
    const c = wall?.center;
    const d = wall?.dimensions;
    return c && d ? c.y + (d.y || 0) / 2 : null;
  }
  const projected = corners.map((p) => ({
    t: projectToParam(p, axes),
    y: p.y,
  }));
  let top = null;
  for (let i = 0; i < projected.length; i++) {
    const a = projected[i];
    const b = projected[(i + 1) % projected.length];
    const lo = Math.min(a.t, b.t);
    const hi = Math.max(a.t, b.t);
    if (t < lo - SPAN_EPS || t > hi + SPAN_EPS) continue;
    const span = b.t - a.t;
    const y =
      Math.abs(span) < SPAN_EPS
        ? Math.max(a.y, b.y)
        : a.y + ((t - a.t) / span) * (b.y - a.y);
    if (top == null || y > top) top = y;
  }
  return top;
}

/**
 * The LOWEST the face gets across the span a piece occupies — the headroom it
 * actually has, not the headroom at its centre.
 */
function wallHeadroomOver(wall, axes, t, halfWidth) {
  let worst = null;
  for (const sample of [t - halfWidth, t, t + halfWidth]) {
    const top = wallFaceTopAt(wall, axes, sample);
    if (top == null) continue;
    if (worst == null || top < worst) worst = top;
  }
  return worst;
}

/**
 * Hang one piece on the widest free run that will take it. Consumes the wall,
 * so a second piece goes elsewhere rather than stacking on the first.
 *
 * `avoidOverTypes` demotes — never forbids — a position that would hang the
 * piece directly above one of those objects. A towel over the toilet is the
 * case: staging guides treat the toilet as the thing to play DOWN (lid closed,
 * nothing on the tank), and hanging the room's largest soft furnishing over it
 * does the opposite; over the vanity it falls in front of the mirror and the
 * counter props. Demoted rather than forbidden because a small bathroom may
 * have nowhere else, and a towel in an awkward spot still beats no towel.
 *
 * Deliberately NOT applied to art: above the toilet is one of the two places
 * the literature actually recommends hanging it.
 */
function hangOnWall(
  room,
  candidates,
  type,
  heightM,
  make,
  usedWallIds,
  {
    avoidOverTypes = null,
    forbidOverTypes = null,
    refuseOverTypes = null,
    preferOverTypes = null,
    // Pieces already staged on these walls. Art may SHARE a wall with the
    // towel — "above the hand-towel rod" is one of the two places the
    // literature sends it — so long as it clears the rod vertically.
    stageAbove = [],
    stageAboveGapM = 0,
    // Pieces already hung on a wall this one may SHARE — allowed on, provided
    // it finishes clear of them vertically.
    //
    // Not `stageAbove`, which is a different offer: that one RAISES the piece
    // until it clears what is below, and works because art has a preferred
    // height rather than a required one. A toilet roll holder's 26 in is a
    // published standard, so it cannot move to make room; the question is only
    // whether what is already there is out of its way. It always is — a towel
    // bar is at 48 in and a ring at 50 — and refusing the wall outright cost
    // the holder 66 of its candidates, because the towels run first and a
    // small bathroom's toilet shares its wall with one of them.
    shareWallClearOf = [],
    shareWallGapM = 0,
    // What `heightM` measures. A towel is hung FROM a bar, so the 48 in
    // standard is the height of its top edge, not of its middle — read as a
    // centre it floats half a towel above the rod, and anything stacked over
    // it inherits the error.
    measureFrom = 'centre',
    // How close to a FORBIDDEN object counts as being in front of it. 0 means
    // "only an actual overlap", which is wrong for a tub — see
    // WET_WALL_CLEARANCE_M.
    //
    // Deliberately NOT applied to the AVOID list. A halo round a tub is right
    // because the tub is a refusal; the same halo round a TOILET makes every
    // wall of a small bathroom equally bad, the severity tie then falls to the
    // first candidate, and the towel lands on the toilet's own wall — worse
    // than the overlap test it replaced. Avoidance stays an overlap question.
    forbidClearanceM = 0,
    // When the preference cannot be met, emit NOTHING rather than falling back
    // to any wall. For art the fallback is right -- art anywhere beats no art.
    // For a hand-towel ring it is not: the ring exists to be beside the basin,
    // and one that lands 6 in from a tub instead is just a second towel in a
    // small bathroom, in the wrong place.
    // The candidate wall must be the one these objects stand against. NOT the
    // same as `preferOverTypes`, which demands a plan OVERLAP: a towel ring
    // wants the vanity's WALL, beside the basin, and the clear run on that
    // wall begins exactly where the vanity ends — so requiring an overlap
    // requires the one position the wall cannot offer.
    requireWallOf = null,
    // What to slide TOWARD along the chosen wall. The preference decides which
    // wall; this decides where on it. They are not the same target: the
    // preference is the vanity, whose centre is where the MIRROR is, while a
    // towel ring wants to be as close to the BASIN as the clear run allows.
    slideToward = null,
    // Slide to just BESIDE these objects along the wall, never across them:
    // `{ objects, gapM }`. The nearer side is taken when both are reachable.
    //
    // Why this is not `slideToward` with a refusal bolted on: the free run a
    // candidate offers is measured in the HANGING band, whose bottom is 52 in
    // off the floor (`computeWallBlockedParams`), so nothing shorter than that
    // is subtracted from it — a toilet is about 30 in and is simply invisible.
    // A toilet roll holder goes at 26 in on the TOILET's wall, so sliding
    // toward the toilet's centre lands it over the cistern inside a run the
    // span logic calls clear, and refusing that position then costs the piece
    // the only wall it is allowed. The target has to be the space beside the
    // fixture, not the fixture.
    slideBeside = null,
    // Mount relative to a nearby surface instead of the floor, when there is
    // one: `{ objects, withinM, aboveM }`.
    liftOver = null,
    // Last word on a candidate, given the fields it would be hung with.
    // Checked BEFORE the wall is claimed, and a refusal moves on to the next
    // candidate rather than ending the search.
    //
    // It exists because claiming is a side effect on `usedWallIds` that the
    // caller cannot undo. The towel ring's "within arm's reach of the basin"
    // test used to run in the caller, on the returned entry: the wall was
    // already claimed by then, so a ring refused for reach burned the one
    // wall the room had and the BAR was refused it too — a bathroom with no
    // towel at all. It also gave up after one wall, when the reach test is
    // exactly the kind a second candidate might pass.
    accept = null,
    // The box to hang, when it is not the type's catalogue box: a single
    // towel hook is the hooks prop at a narrower width.
    dims: dimsOverride = null,
  } = {}
) {
  const dims = dimsOverride || stagingPropDimsM(type);
  if (!dims) return null;
  const floorPoly = getFloorPolygon(room);
  const floorY = getFloorY(room);
  // A type set, or a predicate when one type list cannot say it (a kitchen's
  // steam sources include every basin `isBasinLike` knows).
  const matching = (types) => {
    if (!types) return [];
    const is =
      typeof types === 'function' ? types : (o) => types.has(objectTypeOf(o));
    return (room.objects || []).filter(
      (o) => o?.center && o?.dimensions && is(o)
    );
  };
  const avoid = matching(avoidOverTypes);
  const forbid = matching(forbidOverTypes);
  const refuse = matching(refuseOverTypes);
  const prefer = matching(preferOverTypes);
  const wallOf = attributeToWalls(
    new Set([...avoid, ...forbid, ...prefer, ...(requireWallOf || [])]),
    candidates
  );
  let demoted = null;
  let fallback = null;

  for (const cand of candidates) {
    // A wall already carrying a piece is only reusable when this piece is
    // allowed to stack above it.
    if (
      usedWallIds.has(cand.wall.id) &&
      !stageAbove.length &&
      !shareWallClearOf.length
    )
      continue;
    if (cand.span.length < dims.x + 2 * WALL_END_MARGIN_M) continue;

    const axes = cand.axes;
    const mid = {
      x: axes.start.x + axes.wallDir.x * (axes.wallLen / 2),
      z: axes.start.z + axes.wallDir.z * (axes.wallLen / 2),
    };
    const facing = inwardFacingAt(mid, axes, floorPoly);
    if (!facing) continue;

    // Where along the wall. The centre of the free run by default — but a
    // preference has to MOVE the piece, not just score where it happened to
    // land: art is wanted over the toilet, and the toilet is almost never at
    // the midpoint of its wall.
    const halfW = dims.x / 2;
    const lo = cand.span.start + WALL_END_MARGIN_M + halfW;
    const hi = cand.span.end - WALL_END_MARGIN_M - halfW;
    if (hi < lo) continue;
    let t = (cand.span.start + cand.span.end) / 2;
    // A slide target is CLAMPED into the run rather than required to fall
    // inside it: the basin is usually behind the mirror, which the free run
    // excludes, so the answer is the nearest clear point to it — which is
    // exactly "as adjacent to the sink as the wall allows".
    const beside = (slideBeside?.objects || []).filter(
      (o) => o?.center && o?.dimensions
    );
    if (beside.length) {
      // The fixture's own extent ALONG this wall, from its rotated footprint,
      // then the nearest clear point just past either end of it.
      let best = null;
      for (const target of beside) {
        const f = footprintFor(
          target.center,
          target.dimensions.x,
          target.dimensions.z,
          target.rotation?.y || 0
        );
        if (!f) continue;
        const ps = f.map((c) => projectToParam(c, axes));
        if (!ps.every(Number.isFinite)) continue;
        const near = Math.min(...ps);
        const far = Math.max(...ps);
        const clear = (slideBeside.gapM || 0) + halfW;
        for (const want of [near - clear, far + clear]) {
          // Inside the run, and genuinely clear of the fixture rather than
          // merely centred past it.
          if (want < lo || want > hi) continue;
          if (want + halfW > near && want - halfW < far) continue;
          const score = Math.abs(want - (near + far) / 2);
          if (!best || score < best.score) best = { t: want, score };
        }
      }
      // Nowhere beside it on THIS wall. Try the next candidate rather than
      // falling back to the middle of the run, which is where the fixture is.
      if (!best) continue;
      t = best.t;
    }
    // Three ways to pick a spot along the wall, in precedence: BESIDE a
    // fixture, SLID toward one, or OVER a preferred one. They were written
    // as `if (!beside && slide) … else prefer`, so a caller passing
    // slideBeside together with preferOverTypes had its beside-the-fixture
    // spot overwritten by the fixture's own centre — the one place
    // slideBeside exists to avoid. Inert today (no caller passes both);
    // fixed so the next one is not bitten.
    const slide = (slideToward || []).filter((o) => o?.center);
    if (beside.length) {
      // `t` is already beside the fixture.
    } else if (slide.length) {
      let best = null;
      for (const target of slide) {
        const tp = projectToParam(target.center, axes);
        const clamped = Math.min(Math.max(tp, lo), hi);
        const gap = Math.abs(clamped - tp);
        if (!best || gap < best.gap) best = { t: clamped, gap };
      }
      if (best) t = best.t;
    } else {
      for (const target of prefer) {
        if (wallOf.get(target) !== cand.wall.id) continue;
        const tp = projectToParam(target.center, axes);
        if (tp >= lo && tp <= hi) {
          t = tp;
          break;
        }
      }
    }
    t = Math.min(Math.max(t, lo), hi);
    const along = {
      x: axes.start.x + axes.wallDir.x * t,
      z: axes.start.z + axes.wallDir.z * t,
    };
    // Stand off the wall FACE: the recorded centreline sits half a thickness
    // behind it.
    const standoff =
      (cand.wall?.dimensions?.z ?? DEFAULT_WALL_THICKNESS_M) / 2 + dims.z / 2;

    // Stack above anything already staged on this wall that this piece would
    // otherwise land on top of, keeping the designers' 6-8 in gap. That rule
    // outranks the 57-60 in centre line when art hangs over something — which
    // is exactly the case here.
    // Which side of the wall the room is on, asked AT THE PIECE rather than at
    // the wall's midpoint. `inwardFacingAt` takes its sign from the direction
    // of the floor's centroid, which is a claim about the room as a whole: on
    // `704C9E88`, a 16-corner bathroom, that put the towel bar on the OUTSIDE
    // face of its wall — outside the room, facing away, invisible from inside.
    // The probe is local and exact where it is decisive; where it is not, the
    // centroid answer stands, which is the behaviour every other room keeps.
    //
    // Only the SIGN moves. The normal itself is `axes.wallNormal` either way,
    // so this does not reintroduce the tilt the file header warns about —
    // that warning is about `inwardNormalAt`'s centroid-pointing vector, which
    // is only perpendicular at one point and is used here for nothing but a
    // sign.
    const side = inwardFacingAtStrict(
      along,
      axes,
      floorPoly,
      cand.wall?.dimensions?.z
    );
    const inward =
      side === 0
        ? facing
        : {
            x: axes.wallNormal.x * side,
            z: axes.wallNormal.z * side,
            rotationY: Math.atan2(
              axes.wallNormal.x * side,
              axes.wallNormal.z * side
            ),
          };
    const centre = {
      x: along.x + inward.x * standoff,
      z: along.z + inward.z * standoff,
    };
    // A towel beside a vanity belongs a fixed distance above the COUNTER, not
    // above the floor: hung to the floor standard it reads as mounted too low
    // whenever the counter is tall. Only fires when a surface is actually
    // near, so a towel by the tub keeps the floor standard.
    let topM = heightM;
    let hemBlocked = false;
    if (liftOver && measureFrom === 'top') {
      for (const surface of liftOver.objects || []) {
        if (!surface?.center || !surface?.dimensions) continue;
        // Distance to the counter's nearest EDGE, not its centre. A towel
        // deliberately avoids the vanity's own wall, so it lands on an
        // adjacent one — easily 36 in from a 48 in counter's midpoint while
        // being inches from its end.
        const f = footprintFor(
          surface.center,
          surface.dimensions.x,
          surface.dimensions.z,
          surface?.rotation?.y || 0
        );
        if (!f) continue;
        let gap = Infinity;
        for (let i = 0; i < f.length; i++) {
          gap = Math.min(
            gap,
            pointToSegmentDistanceXZ(centre, f[i], f[(i + 1) % f.length])
          );
        }
        if (gap > liftOver.withinM) continue;
        const surfaceTop =
          surface.center.y + (surface.dimensions.y || 0) / 2 - floorY;
        // Two rules, both measured from the surface: the MOUNT sits a fixed
        // distance above it, and the HEM must clear it. The second is what
        // stops an 18 in drape lying on the counter it is hung over.
        const forMount = surfaceTop + liftOver.aboveM;
        const forHem = surfaceTop + (liftOver.hemClearanceM || 0) + dims.y;
        const wanted = Math.max(forMount, forHem);
        // A cap that clamps BELOW the hem requirement silently reinstates the
        // collision the requirement exists to prevent. Refuse the candidate
        // instead and let the search find a wall where it fits.
        if (liftOver.hemClearanceM && wanted > (liftOver.maxM ?? Infinity)) {
          hemBlocked = true;
          break;
        }
        topM = Math.max(topM, Math.min(wanted, liftOver.maxM ?? Infinity));
      }
    }
    if (hemBlocked) continue;
    let height = measureFrom === 'top' ? topM - dims.y / 2 : heightM;
    for (const below of stageAbove) {
      if (!below?.center || !below?.dimensions) continue;
      if (!(below.parentWallIds || []).includes(cand.wall.id)) continue;
      const belowTop = below.center.y + below.dimensions.y / 2 - floorY;
      const stacked = belowTop + stageAboveGapM + dims.y / 2;
      if (stacked > height) height = stacked;
    }

    // Does `o`'s footprint, projected onto this wall, overlap the piece's own
    // extent along it (widened by `margin` each side)?
    const overlapsAlongWall = (o, margin = 0) => {
      const f = footprintFor(
        o.center,
        o.dimensions.x,
        o.dimensions.z,
        o?.rotation?.y || 0
      );
      if (!f) return false;
      const ts = f.map((p) => projectToParam(p, axes));
      return (
        Math.max(...ts) > t - halfW - margin &&
        Math.min(...ts) < t + halfW + margin
      );
    };

    // Sharing a wall is only on offer while this piece stays out of the way of
    // what is already hung on it — out of its way in BOTH directions: a clash
    // needs the two to overlap along the wall as well as vertically. The
    // vertical test alone refused a roll holder a wall whose ring hung 0.8 m
    // further along it, because the ring's bottom and the holder's top plus
    // the gap met by an inch; and it would have let a holder hang directly
    // under a ring whose band happened to clear it.
    if (shareWallClearOf.length) {
      const myBottom = floorY + height - dims.y / 2;
      const myTop = floorY + height + dims.y / 2;
      const clashes = shareWallClearOf.some((other) => {
        if (!other?.center || !other?.dimensions) return false;
        if (!(other.parentWallIds || []).includes(cand.wall.id)) return false;
        if (!overlapsAlongWall(other, shareWallGapM)) return false;
        const otherBottom = other.center.y - other.dimensions.y / 2;
        const otherTop = other.center.y + other.dimensions.y / 2;
        return (
          myTop + shareWallGapM > otherBottom &&
          myBottom - shareWallGapM < otherTop
        );
      });
      if (clashes) continue;
    }

    // The wall has to actually BE there at this height. Under a roof a wall is
    // a triangle or a pentagon and `dimensions.y` is only its tallest point.
    const faceTop = wallHeadroomOver(cand.wall, axes, t, halfW);
    if (faceTop != null && floorY + height + dims.y / 2 > faceTop - SPAN_EPS) {
      continue;
    }

    const fields = {
      center: {
        x: centre.x,
        y: floorY + height,
        z: centre.z,
      },
      dimensions: dims,
      // The SAME decision that placed it. Taking the offset from one source
      // and the rotation from another is what put 59% of dorm's wall decor
      // facing into the wall; see this file's header.
      rotation: { x: 0, y: inward.rotationY, z: 0 },
      parentWallIds: [cand.wall.id],
    };

    // "Standing over" something is an ALONG-THE-WALL question, not a
    // footprint intersection. A framed print is an inch deep and hangs in
    // front of the wall face, so its footprint clears anything standing in
    // front of it by whatever gap the scan recorded — and every scanned
    // fixture has one. So: is the object against THIS wall, and does its
    // extent along the wall overlap the piece's?
    // PREFERENCE asks "is this piece on the wall that object belongs to", so it
    // is gated on the nearest-wall attribution: a corner fixture is near two
    // walls and must only satisfy the preference on its own.
    const standsOverOnThisWall = (list) =>
      list.some((o) => wallOf.get(o) === cand.wall.id && overlapsAlongWall(o));

    // AVOIDANCE asks "does this piece hang over that object" — which has
    // nothing to do with which wall the object is nearest. Gating it on the
    // attribution is how a towel hung over a bathtub that happened to be
    // attributed to the NEXT wall along: the tub was simply invisible to the
    // test.
    //
    // But it is NOT a plan overlap of the two footprints either, which is
    // what this used to ask. A towel is 3.4 in deep and hangs on the wall
    // face; a scanned vanity sits an inch or two off that face, so the two
    // never intersect and the test answered "not over it" for a towel whose
    // hem finished 19 in BELOW the worktop. 43 corpus cases. The same wrong
    // predicate in the corpus invariant reported them all as PASS.
    //
    // It is the question the art rule already asks: does the object's extent
    // ALONG the wall overlap the piece's — and is the object against this
    // wall at all, so that something across the room whose projection happens
    // to land here does not count.
    const standsOver = (list) =>
      list.some(
        (o) =>
          objectGapToWall(o, cand) <= AGAINST_WALL_M && overlapsAlongWall(o)
      );

    const own = footprintFor(fields.center, dims.x, dims.z, fields.rotation.y);

    /** Smallest distance between two footprints; 0 when they overlap. */
    const gapBetween = (a, b) => {
      if (doPolygonsOverlap(a, b)) return 0;
      let best = Infinity;
      for (const [p, q] of [
        [a, b],
        [b, a],
      ]) {
        for (const pt of p) {
          for (let i = 0; i < q.length; i++) {
            best = Math.min(
              best,
              pointToSegmentDistanceXZ(pt, q[i], q[(i + 1) % q.length])
            );
          }
        }
      }
      return best;
    };

    /** In front of one of these, within `clearance` of it. */
    const hangsNear = (list, clearance) =>
      own != null &&
      list.some((o) => {
        const { x, z } = planDims(o);
        const f = footprintFor(o.center, x, z, o?.rotation?.y || 0);
        return f != null && gapBetween(own, f) <= clearance;
      });

    const hangsOver = (list) =>
      own != null &&
      list.some((o) => {
        const { x, z } = planDims(o);
        const f = footprintFor(o.center, x, z, o?.rotation?.y || 0);
        return f != null && doPolygonsOverlap(own, f);
      });

    if (requireWallOf) {
      const onTheirWall = requireWallOf.some(
        (o) => wallOf.get(o) === cand.wall.id
      );
      if (!onTheirWall) continue;
    }

    // A hard refusal, not a demotion: a framed print inside a tub or shower
    // surround is in the spray and the steam. Better no art than ruined art.
    if (forbid.length && hangsNear(forbid, forbidClearanceM)) continue;

    // Also a hard refusal, but only DIRECTLY over. `forbid` is for a fixture
    // that claims a SECTION of wall -- you cannot reach a towel hung beside a
    // tub without leaning across it, so the tub takes the wall either side of
    // itself too. A toilet claims nothing: the objection is to the towel
    // hanging IN it, so the test is the overlap and the halo would only cost
    // the piece walls it could have used. (Measured: an 18 in halo on this
    // set cost 6 of 23 corpus rooms their bath towel for no gain.)
    if (refuse.length && standsOver(refuse)) continue;

    if (avoid.length && standsOver(avoid)) {
      // Keep the LEAST BAD fallback, not the first. Candidates arrive widest
      // free run first, so "first" means the roomiest wall — which is how a
      // towel ended up over a tub on a 62 in wall while 36 in of clear wall
      // sat beside the vanity.
      // Graded by the SAME test that demoted the candidate. It used to
      // filter by `hangsOver` (a footprint intersection) after demoting on
      // `standsOver` (along-wall overlap), so a vanity a few inches off its
      // wall matched neither, the filter came back empty and
      // `Math.max()` of nothing is -Infinity — a demotion nothing could
      // ever displace, and the towel went by candidate order after all.
      const severity = Math.max(
        ...avoid
          .filter((o) => standsOver([o]))
          .map((o) => AVOID_SEVERITY[objectTypeOf(o)] ?? 2)
      );
      if (!demoted || severity < demoted.severity) {
        demoted = { fields, wallId: cand.wall.id, severity };
      }
      continue;
    }

    // Above the toilet is one of the two places the literature actually sends a
    // bathroom's art, and it is usually the only dead wall in the room. Take it
    // when it is on offer; otherwise keep the first workable spot and carry on
    // looking for one.
    if (prefer.length && !standsOverOnThisWall(prefer)) {
      if (!fallback) fallback = { fields, wallId: cand.wall.id };
      continue;
    }

    if (accept && !accept(fields)) continue;

    usedWallIds.add(cand.wall.id);
    return make(fields);
  }

  for (const spare of [fallback, demoted]) {
    if (spare && (!accept || accept(spare.fields))) {
      usedWallIds.add(spare.wallId);
      return make(spare.fields);
    }
  }
  return null;
}

/**
 * Stand a floor prop AGAINST a wall.
 *
 * A basket sitting out in the middle of a bathroom reads as something somebody
 * dropped, not as styling — floor props belong against a wall.
 *
 * WHICH wall is not arbitrary. Designers put a towel basket in the drying zone
 * beside the tub or shower, or by the vanity within reach of the sink; "an
 * empty corner" is the third answer and nobody puts one by the door. So the
 * caller passes an `anchor` — the tub or shower if the room has one, else the
 * vanity — and walls are ranked by how close they come to it, with the spot
 * along the wall chosen nearest it too.
 *
 * The free span used to rank walls is measured in the HANGING band, which says
 * nothing about what is on the floor against them, so each candidate position
 * is tested against the room's floor-level objects directly.
 *
 * Door walls are DEMOTED, not skipped. Skipping them outright throws away the
 * whole wall because of one opening — on a 90 in wall with a 28 in door at one
 * end that is 62 in of good floor discarded, which in a small bathroom is
 * often the only place a basket could stand. The caller's `isClear` already
 * keeps it out of the swing, which is the thing that actually matters.
 */
function placeAgainstWall(room, candidates, dims, opts = {}) {
  // Candidates arrive one per free RUN of a wall (for the hanging band), but
  // a floor prop sweeps the WHOLE wall at floor level, so a wall split into
  // three runs would be swept three times for the same answer. One per wall.
  const seenWalls = new Set();
  candidates = (candidates || []).filter((c) => {
    const id = c?.wall?.id;
    if (!id || seenWalls.has(id)) return false;
    seenWalls.add(id);
    return true;
  });
  const {
    anchor = null,
    anchorFootprint = null,
    preferWallIds = new Set(),
    doorWallIds = new Set(),
    isClear = () => true,
  } = opts;
  const floorPoly = getFloorPolygon(room);
  const floorY = getFloorY(room);

  // Rank by distance to the anchor's nearest EDGE where we have its footprint,
  // not to its centre. A tub's centre is a point inside the tub, and the floor
  // nearest it is not the drying zone -- measured centre-wise the basket landed
  // 65 in from the tub in a room whose whole rule is "beside the tub".
  const anchorPoints =
    anchorFootprint && anchorFootprint.length
      ? anchorFootprint
      : anchor
        ? [anchor]
        : [];
  const anchorDistance = (cand) => {
    const end = {
      x: cand.axes.start.x + cand.axes.wallDir.x * cand.axes.wallLen,
      z: cand.axes.start.z + cand.axes.wallDir.z * cand.axes.wallLen,
    };
    let best = Infinity;
    for (const p of anchorPoints) {
      best = Math.min(best, pointToSegmentDistanceXZ(p, cand.axes.start, end));
    }
    return best;
  };

  const ordered = [...candidates].sort((a, b) => {
    if (anchor) return anchorDistance(a) - anchorDistance(b);
    return (
      (preferWallIds.has(b.wall.id) ? 1 : 0) -
      (preferWallIds.has(a.wall.id) ? 1 : 0)
    );
  });

  // Door walls last, but not excluded: `isClear` holds the swing.
  const byDoor = [
    ...ordered.filter((c) => !doorWallIds.has(c.wall.id)),
    ...ordered.filter((c) => doorWallIds.has(c.wall.id)),
  ];
  for (const cand of byDoor) {
    const axes = cand.axes;
    const mid = {
      x: axes.start.x + axes.wallDir.x * (axes.wallLen / 2),
      z: axes.start.z + axes.wallDir.z * (axes.wallLen / 2),
    };
    const facing = inwardFacingAt(mid, axes, floorPoly);
    if (!facing) continue;

    const standoff =
      (cand.wall?.dimensions?.z ?? DEFAULT_WALL_THICKNESS_M) / 2 +
      dims.z / 2 +
      FLOOR_WALL_GAP_M;
    const half = Math.max(dims.x, dims.z) / 2;
    const lo = half + WALL_END_MARGIN_M;
    const hi = axes.wallLen - half - WALL_END_MARGIN_M;
    if (hi <= lo) continue;

    // Start at the point on this wall nearest the anchor — the tub, or the
    // vanity — and walk outward. With no anchor, fall back to the centre of
    // whatever is hung on the wall.
    // The spot along the wall: the projection of whichever anchor point is
    // nearest THIS wall -- i.e. the corner of the tub closest to it, which is
    // where you step out and reach.
    let nearestToWall = null;
    let nearestGap = Infinity;
    const wallEnd = {
      x: axes.start.x + axes.wallDir.x * axes.wallLen,
      z: axes.start.z + axes.wallDir.z * axes.wallLen,
    };
    for (const p of anchorPoints) {
      const gap = pointToSegmentDistanceXZ(p, axes.start, wallEnd);
      if (gap < nearestGap) {
        nearestGap = gap;
        nearestToWall = p;
      }
    }
    const preferred = nearestToWall
      ? projectToParam(nearestToWall, axes)
      : (cand.span.start + cand.span.end) / 2;
    const target = Math.min(Math.max(preferred, lo), hi);
    const steps = Math.ceil((hi - lo) / FLOOR_SAMPLE_STEP_M);
    for (let i = 0; i <= steps; i++) {
      for (const dir of i === 0 ? [1] : [1, -1]) {
        const t = target + dir * i * FLOOR_SAMPLE_STEP_M;
        if (t < lo || t > hi) continue;
        const point = {
          x: axes.start.x + axes.wallDir.x * t + facing.x * standoff,
          z: axes.start.z + axes.wallDir.z * t + facing.z * standoff,
        };
        if (!isClear(point)) continue;
        return {
          center: { x: point.x, y: floorY + dims.y / 2, z: point.z },
          dimensions: dims,
          rotation: { x: 0, y: facing.rotationY, z: 0 },
          againstWallId: cand.wall.id,
        };
      }
    }
  }
  return null;
}

module.exports = {
  rankWallsByFreeRun,
  hangOnWall,
  placeAgainstWall,
  wallFaceTopAt,
};
